import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/home/Footer';
import useAuthStore, { isTokenExpired } from '../../stores/authStore';
import StepIndicator from './StepIndicator';
import EmployerAuthStep from './EmployerAuthStep';
import CompanyInfoStep from './CompanyInfoStep';
import JobDetailsStep from './JobDetailsStep';
import JobPaymentStep from './JobPaymentStep';
import {
  saveCompany,
  getCompany,
  saveJobDraft,
  createJobPayment,
} from './postJobApi';
import './PostJobWizard.css';

const STEPS = ['Company info', 'Job details', 'Payment'];

function PostJobWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuthStore();

  const [step, setStep] = useState(Number(searchParams.get('step')) || 0);
  const [companyData, setCompanyData] = useState({});
  const [jobData, setJobData] = useState({ plan: searchParams.get('plan') || 'basic' });
  const [jobPosting, setJobPosting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [draftSaved, setDraftSaved] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const authed = isAuthenticated() && token && !isTokenExpired(token);
  const isEmployer = user?.accountType === 'employer';

  useEffect(() => {
    if (!authed) {
      setStep(0);
      return;
    }

    if (!isEmployer) {
      setStep(0);
      return;
    }

    const urlStep = Number(searchParams.get('step'));
    if (urlStep >= 1 && urlStep <= 3) {
      setStep(urlStep);
    }

    const loadExisting = async () => {
      setLoadingDraft(true);
      try {
        const companyRes = await getCompany(token);
        if (companyRes.company) {
          setCompanyData(companyRes.company);
          if (!urlStep) {
            setStep(1);
          }
        } else if (!urlStep) {
          setStep(1);
        }
      } catch (err) {
        console.error(err);
        if (!urlStep) setStep(1);
      } finally {
        setLoadingDraft(false);
      }
    };

    loadExisting();
  }, [authed, token, searchParams, isEmployer]);

  useEffect(() => {
    if (step > 0) {
      setSearchParams({ step: String(step) });
    } else {
      setSearchParams({});
    }
  }, [step, setSearchParams]);

  const handleCompanyNext = async (form) => {
    setSaving(true);
    setError('');
    try {
      const data = await saveCompany(token, form);
      setCompanyData(data.company);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const buildJobPayload = (form) => ({
    id: jobPosting?._id,
    title: form.title,
    department: form.department,
    location: form.location,
    type: form.type,
    salaryFrom: form.salaryFrom ?? null,
    salaryTo: form.salaryTo ?? null,
    salaryCurrency: form.salaryCurrency || 'USD',
    salaryStructure: form.salaryStructure || 'annual',
    description: form.description,
    jdFileUrl: form.jdFileUrl || '',
    jdFileName: form.jdFileName || '',
    skills: Array.isArray(form.skills) ? form.skills.slice(0, 10) : [],
    responsibilities: [],
    requirements: [],
    benefits: [],
    applicationEmail: form.applicationEmail,
    plan: form.plan,
    startDate: form.startDate || null,
    deadlineDate: form.deadlineDate || null,
  });

  const handleJobNext = async (form) => {
    setSaving(true);
    setError('');
    setDraftSaved(false);
    try {
      const data = await saveJobDraft(token, buildJobPayload(form));
      setJobData(form);
      setJobPosting(data.jobPosting);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleJobSaveDraft = async (form) => {
    setSaving(true);
    setError('');
    setDraftSaved(false);
    try {
      const data = await saveJobDraft(token, buildJobPayload(form));
      setJobData(form);
      setJobPosting(data.jobPosting);
      setDraftSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePay = async ({ paymentMethod, crypto }) => {
    if (!jobPosting?._id) {
      setError('Job posting not found. Please go back and save job details again.');
      return;
    }

    setPaying(true);
    setError('');
    try {
      const data = await createJobPayment(token, {
        jobPostingId: jobPosting._id,
        paymentMethod,
        crypto,
        email: user?.email,
      });

      if (data.free && data.successUrl) {
        window.location.href = data.successUrl;
        return;
      }

      if (paymentMethod === 'card' && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      if (paymentMethod === 'crypto' && data.cryptoUrl) {
        window.location.href = data.cryptoUrl;
        return;
      }

      setError('Secure checkout is not available yet. Please contact support.');
    } catch (err) {
      setError(err.message);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="post-job-page">
      <div className="post-job-container">
        <div className="post-job-hero">
          <p className="post-job-eyebrow">Post a job</p>
          <h1>Publish your startup role on CofounderHubs</h1>
          <p>Sign up with a work email, add company details, describe the role, and pay to publish.</p>
        </div>

        {authed && isEmployer && step > 0 && (
          <StepIndicator currentStep={step} steps={STEPS} />
        )}

        {loadingDraft && authed && isEmployer && step === 0 && (
          <div className="post-job-card"><p>Loading your company profile...</p></div>
        )}

        {!authed && (
          <EmployerAuthStep onAuthenticated={() => setStep(1)} />
        )}

        {authed && !isEmployer && (
          <div className="post-job-card">
            <div className="post-job-card-header">
              <p className="post-job-eyebrow">Employer access required</p>
              <h2>Candidate accounts cannot post jobs</h2>
              <p>
                Your current account is a candidate profile. To post jobs, create or sign in to a company
                employer account using a work email.
              </p>
            </div>
            <div className="post-job-actions">
              <button type="button" className="post-job-btn secondary" onClick={() => navigate('/')}>
                Back to Home
              </button>
              <button type="button" className="post-job-btn primary" onClick={() => useAuthStore.getState().signout()}>
                Sign out and use employer account
              </button>
            </div>
          </div>
        )}

        {authed && isEmployer && step === 1 && (
          <CompanyInfoStep
            initialData={companyData}
            userEmail={user?.email}
            token={token}
            onNext={handleCompanyNext}
            saving={saving}
          />
        )}

        {authed && isEmployer && step === 2 && (
          <JobDetailsStep
            initialData={{ ...jobData, applicationEmail: jobData.applicationEmail || companyData.workEmail }}
            token={token}
            onBack={() => setStep(1)}
            onNext={handleJobNext}
            onSaveDraft={handleJobSaveDraft}
            saving={saving}
          />
        )}

        {authed && isEmployer && step === 3 && (
          <JobPaymentStep
            jobPosting={jobPosting || jobData}
            onBack={() => setStep(2)}
            onPay={handlePay}
            loading={paying}
            error={error}
          />
        )}

        {error && step !== 3 && authed && isEmployer && (
          <div className="post-job-error post-job-error-inline">{error}</div>
        )}

        {draftSaved && step === 2 && authed && isEmployer && !error && (
          <div className="post-job-success-inline">Draft saved. You can continue later from step 2.</div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default PostJobWizard;
